/**
 * EvalEditorialCoaching — Slice 1d editorial coaching layout.
 *
 * Replaces the CoachingNarrativeCard presentation on the redesigned results
 * page with a magazine-style editorial layout:
 *
 *   1. "EDITORIAL COACHING" eyebrow label
 *   2. Fraunces lede — overall_summary rendered as an editorial paragraph
 *      (first-letter accent via CSS ::first-letter).
 *   3. COMPACT VISUAL READ (owner D2) — a 1-2 line always-visible distilled
 *      takeaway from the most important deterministic or Gemini observation.
 *      NOT a re-dump of the full list — just the single strongest signal.
 *   4. Numbered improvement tips with playable timestamp chips (→ seek).
 *   5. Two quiet lists: strengths (left) + focus on (right).
 *   6. Score Breakdown — words-first, all roles (see below).
 *
 * TOKEN CONTRACT (RESULTS_THEME_CONVENTION.md):
 *   Every colour, radius, and font reference goes through a --results-* or
 *   --font-results-* variable.  No hex values, no raw font names, no
 *   Tailwind color utilities.  Rendered inside EvalResultsPageShell.
 *
 * N-3 DEDUP:
 *   When overall_summary is a single sentence, the extracted headline equals
 *   the full body.  Guard: if headline === bodyText (normalised), render only
 *   the lede, skip the sub-body paragraph to prevent rendering the same
 *   sentence twice.
 *
 * WORDS-FIRST WITH EXPANDABLE DETAILS (2026-09-21 report redesign, decision 2):
 *   RETIRES the prior `showScoreDecomposition={isCoach}` ruling. The Score
 *   Breakdown's word-level content (`area` + `explanation` per item) is now
 *   always visible to every role — it is not gated at all. Only the numeric
 *   detail (points_lost, measured_value, expected_value, and the BD-7
 *   raw-match->graded mapping) sits behind the "Score Details" toggle, which
 *   defaults closed for every role. `showScoreDecomposition` is kept as a
 *   caller-level opt-out (e.g. no data) but is no longer derived from
 *   `isCoach` at the EvaluationResults.tsx call site.
 *
 * COMPACT VISUAL READ SOURCE PRIORITY:
 *   1. First non-plausibility-flagged FAULT deterministic observation (strong
 *      or moderate band) from the first phase — these are measured, not AI.
 *   2. First Gemini observation with severity === 'critical' or 'significant'.
 *   3. First Gemini observation (any severity).
 *   Fallback: absent entirely when neither source has data.
 */

import { useState } from 'react';
import type {
  LLMExplanation,
  ScoreDecompositionItem,
  GeminiErrorObservation,
  GradeScaleApplied,
} from '@/types/api';
import type { DeterministicObservation } from './MovementAnalysis';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EvalEditorialCoachingProps {
  /** LLM-generated coaching explanation object */
  narrative?: LLMExplanation | null;
  /**
   * Deterministic observations from metadata.deterministic_observations.
   * Used to derive the compact visual read (owner D2) — first strong fault.
   */
  deterministicObservations?: DeterministicObservation[] | null;
  /** Gemini Vision observations — used as fallback for compact visual read */
  geminiObservations?: GeminiErrorObservation[] | null;
  /**
   * Score decomposition items from llm_explanation. Word-level content
   * (area + explanation) renders for every role; the numeric fields sit
   * behind the in-card "Score Details" toggle (decision 2, 2026-09-21).
   */
  scoreDecomposition?: ScoreDecompositionItem[];
  /**
   * BD-7 grade-feel scoring (track B contract) — the pre-grade measurement M.
   * Paired with gradeScaleApplied to render the "match M → score G" mapping
   * inside Score Details when a per-template grade scale is active.
   */
  rawMatchScore?: number | null;
  /**
   * BD-7 grade-feel scoring (track B contract) — audit trail for whether the
   * grade scale applied. Absent/inactive → Score Details renders exactly as
   * it did before this field existed (no letters, no bands, ever).
   */
  gradeScaleApplied?: GradeScaleApplied | null;
  /**
   * The evaluation's displayed overall score (already the graded number when
   * gradeScaleApplied.active — rule #1: Overall renders unchanged). Used only
   * to show the right-hand side of the raw→graded mapping line.
   */
  overallScore?: number | null;
  /**
   * Called when the user clicks a video timestamp chip on an improvement tip.
   * Receives source_frame_pct (0-100) so the parent can seek the video.
   */
  onSeekToFramePct?: (pct: number) => void;
  /**
   * When false, suppresses the Score Breakdown block entirely regardless of
   * data. Defaults to true. As of 2026-09-21 (decision 2) this is a plain
   * caller opt-out, not a role gate — do not derive it from `isCoach`.
   */
  showScoreDecomposition?: boolean;
  /** When true, renders a skeleton loading placeholder. */
  loading?: boolean;
  /** Error string shown when narrative failed to generate. */
  error?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Normalise a sentence for dedup comparison: trim, collapse interior
 * whitespace, strip trailing punctuation so "Good form." === "Good form".
 */
function normSentence(s: string): string {
  return s.trim().replace(/\s+/g, ' ').replace(/[.!?]+$/, '').toLowerCase();
}

/**
 * Derive the compact visual read from available data sources.
 *
 * Priority:
 *  1. First non-plausibility-flagged fault with magnitude_band
 *     'strong' or 'moderate'.
 *  2. First Gemini observation with severity 'critical' or 'significant'.
 *  3. First Gemini observation (any severity).
 *
 * Returns null when no suitable source exists.
 */
function deriveVisualRead(
  detObs: DeterministicObservation[] | null | undefined,
  geminiObs: GeminiErrorObservation[] | null | undefined
): string | null {
  // Priority 1: strong/moderate deterministic fault
  if (detObs && detObs.length > 0) {
    const strong = detObs.find(
      (o) =>
        !o.is_positive &&
        !o.is_plausibility_flagged &&
        (o.magnitude_band === 'strong' || o.magnitude_band === 'moderate')
    );
    if (strong) return strong.deterministic_sentence;
  }

  // Priority 2: critical/significant Gemini
  if (geminiObs && geminiObs.length > 0) {
    const critical = geminiObs.find(
      (o) => o.severity === 'critical' || o.severity === 'significant'
    );
    if (critical) return critical.visual_description;
    // Priority 3: any Gemini
    return geminiObs[0].visual_description;
  }

  return null;
}

// ── Skeleton placeholder ──────────────────────────────────────────────────────

function SkeletonBlock({ w, h }: { w: string; h: string }) {
  return (
    <div
      aria-hidden="true"
      className="animate-pulse rounded-md"
      style={{
        backgroundColor: 'var(--results-surface-raised-2)',
        width: w,
        height: h,
      }}
    />
  );
}

function CoachingSkeleton() {
  return (
    <section
      aria-label="AI Coaching loading"
      style={{
        backgroundColor: 'var(--results-surface-raised-1)',
        border: '1px solid var(--results-surface-line)',
        borderRadius: 'var(--results-radius-card)',
        padding: 'clamp(28px, 5vw, 48px) clamp(20px, 4vw, 40px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <SkeletonBlock w="6rem" h="0.75rem" />
      <SkeletonBlock w="75%" h="1.75rem" />
      <SkeletonBlock w="100%" h="1rem" />
      <SkeletonBlock w="90%" h="1rem" />
      <SkeletonBlock w="80%" h="1rem" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SkeletonBlock w="60%" h="0.875rem" />
          <SkeletonBlock w="100%" h="0.75rem" />
          <SkeletonBlock w="85%" h="0.75rem" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SkeletonBlock w="60%" h="0.875rem" />
          <SkeletonBlock w="100%" h="0.75rem" />
          <SkeletonBlock w="90%" h="0.75rem" />
        </div>
      </div>
    </section>
  );
}

// ── Frame timestamp chip ──────────────────────────────────────────────────────

interface FrameChipProps {
  pct: number;
  onClick: (pct: number) => void;
}

function FrameChip({ pct, onClick }: FrameChipProps) {
  return (
    <button
      type="button"
      onClick={() => onClick(pct)}
      title={`Seek video to ${Math.round(pct)}% of the evaluation`}
      aria-label={`Jump to ${Math.round(pct)}% in video`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '2px 8px',
        borderRadius: 'var(--results-radius-pill)',
        border: '1px solid var(--results-accent-warn)',
        backgroundColor: 'color-mix(in srgb, var(--results-accent-warn) 12%, transparent)',
        color: 'var(--results-accent-warn)',
        fontSize: '11px',
        fontFamily: 'var(--font-results-mono)',
        fontWeight: 600,
        cursor: 'pointer',
        flexShrink: 0,
        lineHeight: 1.4,
        verticalAlign: 'middle',
        marginLeft: '6px',
        transition: 'background-color 0.15s',
      }}
      onMouseOver={(e) => {
        (e.currentTarget as HTMLButtonElement).style.backgroundColor =
          'color-mix(in srgb, var(--results-accent-warn) 22%, transparent)';
      }}
      onMouseOut={(e) => {
        (e.currentTarget as HTMLButtonElement).style.backgroundColor =
          'color-mix(in srgb, var(--results-accent-warn) 12%, transparent)';
      }}
    >
      <svg width="8" height="8" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
        <polygon points="2,1 9,5 2,9" />
      </svg>
      at {Math.round(pct)}%
    </button>
  );
}

// ── Grade-scale note (coach-only, BD-7 story 8) ────────────────────────────────
//
// Rendered inside "Score Details" ABOVE the per-item breakdown, only when
// gradeScaleApplied.active is true. Two pieces, both required by the track C
// contract:
//   1. A quiet one-line explanation (verbatim copy from the contract).
//   2. The raw→graded mapping ("match 53.1 → score 84"), matching the
//      breakdown's own arrow idiom (→) already used elsewhere in this file
//      (see the next_steps list above).
// Inactive/absent gradeScaleApplied → this component is never rendered, and
// nothing else in Score Details changes — rule #1 (no letters/bands, ever)
// holds trivially since neither piece of copy contains a grade letter.

interface GradeScaleNoteProps {
  rawMatchScore: number;
  overallScore: number;
}

function GradeScaleNote({ rawMatchScore, overallScore }: GradeScaleNoteProps) {
  return (
    <div
      data-testid="grade-scale-note"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '10px 14px',
        backgroundColor: 'var(--results-surface-raised-2)',
        border: '1px solid var(--results-surface-line)',
        borderRadius: 'var(--results-radius-sm)',
      }}
    >
      <p
        style={{
          fontFamily: 'var(--font-results-body)',
          fontSize: '12px',
          color: 'var(--results-text-dim)',
          margin: 0,
        }}
      >
        Overall reflects your coach's grading scale; component scores are raw match measurements.
      </p>
      <p
        style={{
          fontFamily: 'var(--font-results-mono)',
          fontSize: '12px',
          fontWeight: 600,
          color: 'var(--results-text-primary)',
          margin: 0,
        }}
      >
        match {rawMatchScore.toFixed(1)}
        <span
          style={{ color: 'var(--results-text-faint)', margin: '0 6px' }}
          aria-hidden="true"
        >
          →
        </span>
        score {Math.round(overallScore)}
      </p>
    </div>
  );
}

// ── Score Breakdown (words-first, all roles) ────────────────────────────────

interface ScoreBreakdownProps {
  items: ScoreDecompositionItem[];
  /**
   * When false, only `area` + `explanation` render (words-first). When true,
   * the numeric points_lost / measured_value / expected_value fields are
   * also shown — the "detail layer" from decision 2. Controlled by the
   * parent's "Score Details" toggle, available to every role.
   */
  showNumericDetail: boolean;
}

function ScoreBreakdown({ items, showNumericDetail }: ScoreBreakdownProps) {
  const pointLossItems = items.filter((item) => item.points_lost > 0);
  const observationItems = items.filter((item) => item.points_lost === 0);

  if (pointLossItems.length === 0 && observationItems.length === 0) return null;

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    backgroundColor: 'var(--results-surface-raised-2)',
    border: '1px solid var(--results-surface-line)',
    borderRadius: 'var(--results-radius-sm)',
    padding: '12px 14px',
  };

  return (
    <div
      data-testid="score-decomposition"
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      {pointLossItems.length > 0 && (
        <div>
          <p
            style={{
              fontFamily: 'var(--font-results-mono)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase' as const,
              color: 'var(--results-text-dim)',
              margin: '0 0 10px',
            }}
          >
            Score Breakdown
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pointLossItems.map((item, idx) => (
              <div key={idx} style={rowStyle}>
                {showNumericDetail && (
                  <span
                    style={{
                      color: 'var(--results-accent-warn)',
                      fontFamily: 'var(--font-results-mono)',
                      fontWeight: 700,
                      fontSize: '13px',
                      flexShrink: 0,
                      marginTop: '2px',
                      minWidth: '42px',
                    }}
                  >
                    -{item.points_lost.toFixed(1)}
                  </span>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-results-body)',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: 'var(--results-text-primary)',
                    }}
                  >
                    {item.area}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-results-body)',
                      fontSize: '12px',
                      color: 'var(--results-text-dim)',
                      marginTop: '3px',
                    }}
                  >
                    {item.explanation}
                  </div>
                  {showNumericDetail && (item.measured_value || item.expected_value) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '5px' }}>
                      {item.measured_value && (
                        <span
                          style={{
                            fontFamily: 'var(--font-results-mono)',
                            fontSize: '11px',
                            color: 'var(--results-accent-bad)',
                          }}
                        >
                          Measured: {item.measured_value}
                        </span>
                      )}
                      {item.expected_value && (
                        <span
                          style={{
                            fontFamily: 'var(--font-results-mono)',
                            fontSize: '11px',
                            color: 'var(--results-accent-good)',
                          }}
                        >
                          Expected: {item.expected_value}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {observationItems.length > 0 && (
        <div>
          <p
            style={{
              fontFamily: 'var(--font-results-mono)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase' as const,
              color: 'var(--results-text-dim)',
              margin: '0 0 10px',
            }}
          >
            Observations
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {observationItems.map((item, idx) => (
              <div key={idx} style={rowStyle}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-results-body)',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: 'var(--results-text-primary)',
                    }}
                  >
                    {item.area}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-results-body)',
                      fontSize: '12px',
                      color: 'var(--results-text-dim)',
                      marginTop: '3px',
                    }}
                  >
                    {item.explanation}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── EvalEditorialCoaching ─────────────────────────────────────────────────────

export default function EvalEditorialCoaching({
  narrative,
  deterministicObservations,
  geminiObservations,
  scoreDecomposition,
  rawMatchScore,
  gradeScaleApplied,
  overallScore,
  onSeekToFramePct,
  showScoreDecomposition = true,
  loading = false,
  error,
}: EvalEditorialCoachingProps) {
  // Track whether the score breakdown collapsed section is visible
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return <CoachingSkeleton />;
  }

  // ── No content ────────────────────────────────────────────────────────────
  const hasNarrative = !!narrative;
  const hasGemini = !!(geminiObservations && geminiObservations.length > 0);

  if (!hasNarrative && !error && !hasGemini) {
    return (
      <section
        aria-label="AI Coaching"
        style={{
          backgroundColor: 'var(--results-surface-raised-1)',
          border: '1px solid var(--results-surface-line)',
          borderRadius: 'var(--results-radius-card)',
          padding: 'clamp(28px, 5vw, 48px) clamp(20px, 4vw, 40px)',
        }}
      >
        <p
          style={{
            fontFamily: 'var(--font-results-mono)',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase' as const,
            color: 'var(--results-text-dim)',
            margin: '0 0 16px',
          }}
        >
          Editorial Coaching
        </p>
        <p
          style={{
            fontFamily: 'var(--font-results-display)',
            fontSize: 'clamp(18px, 2.5vw, 24px)',
            fontWeight: 400,
            fontStyle: 'normal',
            color: 'var(--results-text-primary)',
            margin: '0 0 10px',
          }}
        >
          Coaching feedback unavailable
        </p>
        <p
          style={{
            fontFamily: 'var(--font-results-body)',
            fontSize: '15px',
            color: 'var(--results-text-dim)',
            margin: 0,
          }}
        >
          AI coaching narrative was not generated for this evaluation.
        </p>
      </section>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error && !hasNarrative) {
    return (
      <section
        aria-label="AI Coaching"
        style={{
          backgroundColor: 'var(--results-surface-raised-1)',
          border: '1px solid var(--results-surface-line)',
          borderRadius: 'var(--results-radius-card)',
          padding: 'clamp(28px, 5vw, 48px) clamp(20px, 4vw, 40px)',
        }}
      >
        <p
          style={{
            fontFamily: 'var(--font-results-mono)',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase' as const,
            color: 'var(--results-text-dim)',
            margin: '0 0 16px',
          }}
        >
          Editorial Coaching
        </p>
        <p
          style={{
            fontFamily: 'var(--font-results-display)',
            fontSize: 'clamp(18px, 2.5vw, 24px)',
            fontWeight: 400,
            fontStyle: 'normal',
            color: 'var(--results-text-primary)',
            margin: '0 0 10px',
          }}
        >
          Coaching feedback unavailable
        </p>
        <p
          style={{
            fontFamily: 'var(--font-results-body)',
            fontSize: '15px',
            color: 'var(--results-accent-bad)',
            margin: 0,
          }}
        >
          {error}
        </p>
      </section>
    );
  }

  // ── N-3 DEDUP: headline vs lede body ──────────────────────────────────────
  // Extract headline (first sentence of overall_summary).
  const rawSummary = narrative?.overall_summary ?? '';
  const headlineSentence = rawSummary.split(/(?<=[.!?])\s+/)[0]?.trim() ?? '';

  // The "lede body" is the full summary paragraphs split by double-newlines.
  // But if the headline is the ONLY sentence in the summary, skip the body
  // paragraph to avoid rendering the same text twice (N-3 dedup).
  const isSingleSentence = normSentence(headlineSentence) === normSentence(rawSummary);

  // Body paragraphs: split on double newlines, then remove the first paragraph
  // ONLY if it would be identical to the headline (single-sentence guard).
  const bodyParas = rawSummary
    .split('\n\n')
    .map((p) => p.trim())
    .filter(Boolean);

  const displayParas: string[] = isSingleSentence
    ? [] // dedup: headline already shows the whole summary
    : bodyParas;

  // ── Compact visual read (owner D2) ────────────────────────────────────────
  const visualRead = deriveVisualRead(deterministicObservations, geminiObservations);

  // ── Improvement tips with timestamp chips ─────────────────────────────────
  const tips = (narrative?.improvement_tips ?? []).map((text, i) => {
    const anchor = narrative?.improvement_tip_anchors?.[i];
    return {
      text,
      source_frame_pct: anchor?.source_frame_pct ?? null,
    };
  });

  // Next steps — no timestamp chips
  const nextSteps = narrative?.next_steps ?? [];

  // Strengths
  const strengths = narrative?.strengths ?? [];

  const hasTips = tips.length > 0 || nextSteps.length > 0;
  const hasStrengths = strengths.length > 0;

  // ── Shared style pieces ────────────────────────────────────────────────────
  const eyebrowStyle: React.CSSProperties = {
    fontFamily: 'var(--font-results-mono)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--results-text-dim)',
    margin: '0 0 20px',
  };

  const columnLabelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-results-mono)',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    margin: '0 0 12px',
  };

  const bodyTextStyle: React.CSSProperties = {
    fontFamily: 'var(--font-results-body)',
    fontSize: '15px',
    lineHeight: 1.65,
    color: 'var(--results-text-dim)',
  };

  const listItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontFamily: 'var(--font-results-body)',
    fontSize: '14px',
    lineHeight: 1.55,
    color: 'var(--results-text-dim)',
  };

  const listStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  return (
    <section
      aria-label="AI Coaching"
      data-testid="editorial-coaching"
      style={{
        backgroundColor: 'var(--results-surface-raised-1)',
        border: '1px solid var(--results-surface-line)',
        borderRadius: 'var(--results-radius-card)',
        padding: 'clamp(28px, 5vw, 48px) clamp(20px, 4vw, 40px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '28px',
      }}
    >
      {/* ── Eyebrow ────────────────────────────────────────────────────────── */}
      <p style={eyebrowStyle}>Editorial Coaching</p>

      {/* ── Fraunces lede (the headline sentence, display font) ─────────────
          D4 rule: upright — no font-style: italic.
          first-letter accent via inline CSS class .coaching-lede::first-letter */}
      {headlineSentence && (
        <div>
          <p
            className="coaching-lede"
            data-testid="coaching-lede"
            style={{
              fontFamily: 'var(--font-results-display)',
              fontSize: 'clamp(20px, 3vw, 28px)',
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: 1.35,
              color: 'var(--results-text-primary)',
              margin: 0,
            }}
          >
            {headlineSentence}
          </p>

          {/* N-3 DEDUP: body paragraphs only when more than one sentence exists */}
          {displayParas.length > 0 && (
            <div
              data-testid="coaching-body-paragraphs"
              style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              {displayParas.map((para, i) => (
                <p key={i} style={{ ...bodyTextStyle, margin: 0 }}>
                  {para}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Compact visual read (owner D2) ───────────────────────────────────
          Always-visible 1-2 line distilled takeaway from the strongest
          deterministic or visual signal.  NOT the full lists.             */}
      {visualRead && (
        <div
          data-testid="compact-visual-read"
          style={{
            borderLeft: '2px solid var(--results-accent-report)',
            paddingLeft: '14px',
          }}
        >
          <p
            style={{
              fontFamily: 'var(--font-results-mono)',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.10em',
              textTransform: 'uppercase' as const,
              color: 'var(--results-accent-report)',
              margin: '0 0 6px',
            }}
          >
            Key observation
          </p>
          <p
            style={{
              fontFamily: 'var(--font-results-body)',
              fontSize: '14px',
              lineHeight: 1.55,
              color: 'var(--results-text-dim)',
              margin: 0,
            }}
          >
            {visualRead}
          </p>
        </div>
      )}

      {/* ── Two-column: strengths + focus-on ─────────────────────────────── */}
      {(hasStrengths || hasTips) && (
        <div className="editorial-coaching-cols">
          {/* LEFT: strengths */}
          {hasStrengths && (
            <div>
              <p style={{ ...columnLabelStyle, color: 'var(--results-accent-good)' }}>
                What you're doing well
              </p>
              <ul style={listStyle} aria-label="Strengths">
                {strengths.map((item, i) => (
                  <li key={i} style={listItemStyle}>
                    <span
                      style={{
                        color: 'var(--results-accent-good)',
                        fontWeight: 700,
                        flexShrink: 0,
                        fontSize: '12px',
                        marginTop: '2px',
                      }}
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* RIGHT: numbered improvement tips + next steps */}
          {hasTips && (
            <div>
              <p style={{ ...columnLabelStyle, color: 'var(--results-accent-warn)' }}>
                Focus on
              </p>
              <ol style={{ ...listStyle, listStyleType: 'none' }} aria-label="Focus areas">
                {tips.map((tip, i) => (
                  <li key={i} style={listItemStyle} data-testid="improvement-tip">
                    <span
                      style={{
                        color: 'var(--results-accent-warn)',
                        fontFamily: 'var(--font-results-mono)',
                        fontWeight: 700,
                        flexShrink: 0,
                        fontSize: '12px',
                        minWidth: '18px',
                        marginTop: '2px',
                      }}
                      aria-hidden="true"
                    >
                      {i + 1}.
                    </span>
                    <span>
                      {tip.text}
                      {tip.source_frame_pct != null && onSeekToFramePct && (
                        <FrameChip pct={tip.source_frame_pct} onClick={onSeekToFramePct} />
                      )}
                    </span>
                  </li>
                ))}
                {nextSteps.map((step, i) => (
                  <li key={`ns-${i}`} style={listItemStyle}>
                    <span
                      style={{
                        color: 'var(--results-text-faint)',
                        fontFamily: 'var(--font-results-mono)',
                        fontWeight: 700,
                        flexShrink: 0,
                        fontSize: '12px',
                        minWidth: '18px',
                        marginTop: '2px',
                      }}
                      aria-hidden="true"
                    >
                      →
                    </span>
                    <span style={{ color: 'var(--results-text-faint)' }}>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* ── Score breakdown — words-first, all roles ─────────────────────────
          2026-09-21 report redesign (decision 2) RETIRES the prior
          `showScoreDecomposition={isCoach}` gate. The word-level content
          (area + explanation per item) is always visible immediately below
          — no click required. Only the numeric detail (points_lost,
          measured/expected values, and the BD-7 raw->graded mapping) sits
          behind the "Score Details" toggle, which defaults closed for every
          role — this is the "expandable details" layer from decision 2.

          BD-7 grade-feel scoring (story 8): when a per-template grade scale
          is active, the raw→graded mapping note renders inside the same
          numeric-detail toggle as the rest of the technical breakdown.
          Inactive/absent gradeScaleApplied → this block contributes nothing;
          unrelated to the words-first change above. */}
      {(() => {
        const hasScoreItems = !!scoreDecomposition && scoreDecomposition.length > 0;
        const hasGradeScaleNote =
          !!gradeScaleApplied?.active && rawMatchScore != null && overallScore != null;
        if (!showScoreDecomposition || (!hasScoreItems && !hasGradeScaleNote)) return null;

        return (
          <div
            data-testid="score-decomposition-section"
            style={{
              borderTop: '1px solid var(--results-surface-line)',
              paddingTop: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {/* Words-first content — always visible, no role gate, no click required */}
            {hasScoreItems && (
              <ScoreBreakdown
                items={scoreDecomposition as ScoreDecompositionItem[]}
                showNumericDetail={breakdownOpen}
              />
            )}

            {(hasScoreItems || hasGradeScaleNote) && (
              <button
                type="button"
                onClick={() => setBreakdownOpen((v) => !v)}
                aria-expanded={breakdownOpen}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-results-mono)',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase' as const,
                  color: 'var(--results-text-dim)',
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transition: 'transform 0.2s',
                    transform: breakdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                Score Details
              </button>
            )}

            {breakdownOpen && hasGradeScaleNote && (
              <GradeScaleNote rawMatchScore={rawMatchScore as number} overallScore={overallScore as number} />
            )}
          </div>
        );
      })()}

      {/* Responsive two-column grid */}
      <style>{`
        .editorial-coaching-cols {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
        }
        @media (max-width: 640px) {
          .editorial-coaching-cols {
            grid-template-columns: 1fr;
            gap: 20px;
          }
        }
        .coaching-lede::first-letter {
          font-size: 1.6em;
          line-height: 0.8;
          float: left;
          margin-right: 4px;
          color: var(--results-accent-report);
          font-weight: 400;
        }
      `}</style>
    </section>
  );
}
